import React from 'react';
import { motion } from 'framer-motion';
import matrixrain from './assets/images/matrixrain.gif';

const SectionTitle = ({ children }) => (
  <h2 className="text-3xl font-bold text-white mb-8 flex items-center gap-3">
    <span className="text-secondary">/</span> {children}
  </h2>
);

export default function MatrixRainAnimation({ onBack }) {
  return (
    <div className="max-w-5xl mx-auto px-6 py-20">
      <button
        type="button"
        onClick={onBack}
        className="inline-flex items-center gap-2 text-secondary hover:text-white font-mono text-sm mb-8"
      >
        ← Back to portfolio
      </button>

      <SectionTitle>Falling Matrix Rain Animation</SectionTitle>

      <div className="grid gap-12 lg:grid-cols-[1fr_0.95fr] mb-14">
        <div className="space-y-6 text-slate-400">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-lg leading-8"
          >
            This browser animation recreates the iconic falling code rain from The Matrix using JavaScript and CSS. It focuses on performance, smooth motion, and a stylized neon visual palette.
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-lg leading-8"
          >
            The final result is a responsive animation that runs smoothly in modern browsers, with adjustable speed, glow effects, and layered depth for a cinematic feel.
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-lg leading-8"
          >
            Built for a school group project, source code available on <a href="https://github.com/Nathan-J26/ENSF400_Project" target="_blank" rel="noreferrer" className="text-primary hover:underline">GitHub</a>. Full, droppable HTML effect available for free here: <a href="https://ethan-klassen.github.io/matrix-rain/" className="text-primary hover:underline">https://ethan-klassen.github.io/matrix-rain/</a>
          </motion.p>
          <div className="rounded-3xl border border-slate-700 bg-slate-900 p-6">
            <h3 className="text-xl font-semibold text-white mb-3">What’s included</h3>
            <ul className="list-disc list-inside space-y-2 text-slate-400">
              <li>Efficient DOM rendering with CSS transforms and opacity fading.</li>
              <li>Responsive styling for desktop and mobile screens.</li>
              <li>Animated particles and glow layers for dynamic depth.</li>
            </ul>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-3xl overflow-hidden border border-slate-700 bg-slate-800 aspect-[16/9] w-full"
        >
          <img
            src={matrixrain}
            alt="Matrix rain animation preview"
            className="w-full h-full object-cover"
          />
        </motion.div>
      </div>

      <div className="rounded-3xl border border-slate-700 bg-slate-800 p-8 space-y-6">
        <div>
          <h3 className="text-2xl font-bold text-white mb-4">Design approach</h3>
          <p className="text-slate-400 leading-7">
            I balanced a neon cyberpunk aesthetic with browser-friendly performance. The animation leverages pure CSS effects with a minimal JavaScript driver to keep CPU usage low while preserving a striking visual style.
          </p>
        </div>
        <div className="grid gap-6 md:grid-cols-2">
          <div>
            <h4 className="text-white font-semibold mb-3">Animation</h4>
            <p className="text-slate-400 leading-7">
              Rows of characters are animated with staggered opacity changes and motion blur-inspired highlights to create a sense of continuous motion.
            </p>
          </div>
          <div>
            <h4 className="text-white font-semibold mb-3">Implementation</h4>
            <p className="text-slate-400 leading-7">
              The implementation focuses on smooth rendering by using requestAnimationFrame and generating only the visible columns needed for the current viewport.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
